import AsyncStorage from '@react-native-async-storage/async-storage';
import {newGestureUid} from '../components/scanpack/boxChampEvents';

// Single owner of the 'logsArray' scan-log queue.
//
// Every read-modify-write of logsArray goes through one lock, so overlapping async callers can't
// clobber each other. After a successful submit, only the logs that were actually sent (the
// caller's temp array) are subtracted from the queue; a log saved while the request was in flight
// stays queued. Removing the whole key on success used to drop those logs, so GPX completed orders
// the server never saw the last scan for.

const KEY = 'logsArray';
const MAX_LOGS = 80;
let chain = Promise.resolve();

const withLock = fn => {
  const run = chain.then(fn, fn);
  chain = run.catch(() => {});
  return run;
};

const readLogs = async () => {
  const raw = await AsyncStorage.getItem(KEY);
  if (!raw || raw === 'undefined') return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
};

const writeLogs = async logs => {
  if (logs.length) {
    await AsyncStorage.setItem(KEY, JSON.stringify(logs));
  } else {
    await AsyncStorage.removeItem(KEY);
  }
};

// Appends a log and resolves to the updated queue.
export const appendLog = log =>
  withLock(async () => {
    const logs = await readLogs();
    if (logs.length >= MAX_LOGS) {
      logs.splice(0, 1);
    }
    logs.push(log?.uid ? log : {...log, uid: newGestureUid()});
    await writeLogs(logs);
    return logs;
  });

// Copy of the queued logs at this moment (the "temp array" a request sends).
export const snapshotLogs = () => withLock(readLogs);

// Removes each sent log once (a log is identified by its full serialized content: time to the ms,
// order_item_id, SKU, event, ...). Logs queued after the snapshot don't match and stay.
export const subtractSentLogs = sentLogs =>
  withLock(async () => {
    const pending = new Map();
    sentLogs.forEach(log => {
      const key = JSON.stringify(log);
      pending.set(key, (pending.get(key) || 0) + 1);
    });
    const remaining = (await readLogs()).filter(log => {
      const key = JSON.stringify(log);
      const count = pending.get(key);
      if (!count) return true;
      pending.set(key, count - 1);
      return false;
    });
    await writeLogs(remaining);
  });
